import { mapState as rootMapState } from 'pinia';
import { usePageStore } from '@/store';
import { numberFormat } from '../../common/format';
import util from '../../common/util';

const filterNameValue = (value, name, ckValue) => {
    return ckValue[name] === value;
};

const filterValue = (value, ckValue) => {
    return value === ckValue;
};

const filter = {
    get(arr) {
        const idx = this.index(...arguments);
        return idx === -1 ? undefined : arr[idx];
    },
    index(arr, condition) {
        if (arguments.length === 3) {
            condition = {
                handler: filterNameValue,
                args: [condition, arguments[2]]
            };
        } else if (
            !util.isFunction(condition) &&
            !util.isFunction(condition.handler)
        ) {
            condition = {
                handler: filterValue,
                args: [condition]
            };
        }
        for (let i = 0, len = arr.length; i < len; i++) {
            if (util.call(condition, null, arr[i])) {
                return i; 
            }
        }
        return -1;
    }
};

export default {
    emits: [
        'visible',
        'doMapEvent',
        'updateMarkerData',
        'updateMarkerEvent',
        'updateMapClickEvent'
    ],
    i18next: {
        ns: 'md-map'
    },
    props: {
        showCollapse: {
            type: Boolean,
            default: true
        },
        replace: {
            type: Boolean,
            default: false
        },
        tabs: {
            type: Array,
            default: () => []
        },
        selectTab: {
            type: String,
            default: ''
        },
        houseLimit: {
            type: Number,
            default: 20
        }
    },
    data: function() {
        return {
            activeType: '',
            collapsed: false,
            showDetail: false,
            detailData: {},
            businessListMap: {},
            schoolGroupActiveIndex: -1,
            businessGroupActiveIndex: -1,
            itemActiveKey: '',
            transformLink2WordPress: util.transformLink2WordPress
        };
    },
    computed: {
        ...rootMapState(usePageStore, {
            "page": store => store.page || {},
            "currencySymbol": store => store.currency?.symbol
        }),
        isMobile() {
            return this.platform.isMobile;
        },
        wordPressSSR({ page }) {
            return page.wordPressSSR;
        }
    },
    watch: {
        collapsed: {
            immediate: true,
            handler: function(newval) {
                this.$emit('visible', !newval);
                this.$nextTick(() => {
                    setTimeout(() => {
                        this.$emit('doMapEvent', function() {
                            this.siteMap &&
                                this.siteMap.mapResize &&
                                this.siteMap.mapResize();
                        });
                    }, 500);
                });
            }
        },
        selectTab(value) {
            let flag = false;
            for (let i = 0, len = this.tabs.length; i < len; i++) {
                if (value === this.tabs[i].type) {
                    flag = true;
                    break;
                }
            }
            if (flag) {
                this.activeType = value;
            }
        },
        activeType(value, oldValue) {
            if (oldValue) {
                this.$emit('updateMarkerData', {
                    [oldValue]: null
                });
            }
            if (value) {
                this.collapsed = false;
                let markerData = filter.get(this.tabs, value, 'type');
                if (!markerData) {
                    return; 
                }
                if (!markerData.data) {
                    let mergeDatas = (markerData.data = []),
                        groups = markerData.groups;
                    for (let i = 0, len = groups.length; i < len; i++) {
                        mergeDatas.push(...groups[i].data);
                    }
                }
                if (value === 'school' && this.schoolGroupActiveIndex !== -1) {
                    markerData = markerData.groups[this.schoolGroupActiveIndex];
                }
                if (
                    value === 'business' &&
                    this.businessGroupActiveIndex !== -1
                ) {
                    markerData =
                        markerData.groups[this.businessGroupActiveIndex];
                }
                this.$emit('updateMarkerData', {
                    [value]: markerData.data
                });
            }
        }
    },
    created() {
        let activeType = this.selectTab;
        this.addAddress2Tabs();
        if (!this.collapsed && this.tabs.length) {
            let flag = false;
            for (let i = 0, len = this.tabs.length; i < len; i++) {
                if (activeType === this.tabs[i].type) {
                    flag = true;
                    break;
                }
            }
            if (!flag) {
                activeType = this.tabs[0].type;
            }
        }
        if (this.collapsed && activeType && this.tabs.length) {
            let flag = false;
            for (let i = 0, len = this.tabs.length; i < len; i++) {
                if (activeType === this.tabs[i].type) {
                    flag = true;
                    break;
                }
            }
            if (!flag) {
                activeType = '';
            }
        }
        if (this.isMobile && activeType === 'business') {
            activeType = 'house';
        }
        this.activeType = activeType;
        const self = this;
        const markerEventMap = {
            house: {
                click: function() {
                    self.onMarkerClick(this, 'house');
                }
            },
            school: {
                mouseEnter: function() {
                    self.onMarkerHover(this);
                },
                mouseLeave: function() {
                    self.onMarkerLeave(this);
                },
                click: function() {
                    self.onMarkerClick(this, 'school');
                }
            },
            business: {
                click: function() {
                    self.onMarkerClick(this, 'business');
                }
            }
        };
        this.$emit('updateMarkerEvent', markerEventMap);
        this.$emit('updateMapClickEvent', function(evt) {
            const target = evt.target;
            if (!util.closest(target, '.map-marker')) {
                self.showDetail = false;
                self.itemLeave(self.activeType);
            }
        });
    },
    methods: {
        addAddress2Tabs() {
            for (let i = 0, len = this.tabs.length; i < len; i++) {
                this.formatAddress(this.tabs[i]);
            }
        },
        formatAddress(tab) {
            let data = tab?.data || [];
            data.forEach(item => {
                if (!item?.address) {
                    return; 
                }
                item.formatAddress = item.address
                    ? item.address.replace(/, */g, ', ')
                    : '';
            });
        },
        schoolGroupClick(index, group) {
            let markerData = filter.get(this.tabs, 'school', 'type');
            if (this.schoolGroupActiveIndex === index) {
                this.schoolGroupActiveIndex = -1;
            } else {
                this.schoolGroupActiveIndex = index;
                markerData = group;
            }
            this.$emit('updateMarkerData', {
                school: markerData.data
            });
        },
        businessGroupClick(index, group, forceShow) {
            let markerData = group.data;
            if (forceShow || this.businessGroupActiveIndex !== index) {
                this.businessGroupActiveIndex = index;
                this.$emit(
                    'updateMarkerData',
                    {
                        business: markerData
                    },
                    true
                );
            } else {
                this.businessGroupActiveIndex = -1;
            }
        },
        itemEnter(type, item, boundaryFlag = true, autoCenter = false) {
            this.itemActiveKey =
                type + '-' + item.latitude + '-' + item.longitude;
            this.$emit('doMapEvent', function() {
                this.siteMap.setActiveMarker(
                    item.latitude,
                    item.longitude,
                    type
                );
                autoCenter &&
                    this.siteMap.setCenter({
                        lat: item.latitude,
                        lng: item.longitude
                    });
                this.removeBoundary();

                //  Get the request interface boundary
                const typeObj = {
                    school: 'school',
                    city: 'city',
                    neighborhood: 'nbr'
                };
                console.log(type);
                if (typeObj[type]) {
                    this.axiosIns
                        .get(
                            `/api-hj/hj-data/v1/boundary?type=${typeObj[type]}&hjId=${item.hjId}`
                        )
                        .then(boundary => {
                            if (
                                boundary &&
                                boundary.data &&
                                boundary.data.length > 0
                            ) {
                                this.showBoundary(boundary, boundaryFlag);
                            }
                        });
                }
            });
        },
        itemLeave(type) {
            this.itemActiveKey = '';
            !this.showDetail &&
                this.$emit('doMapEvent', function() {
                    this.removeBoundary();
                    this.siteMap.clearActiveMarker(type);
                });
        },
        schoolItemClick(item) {
            const detailData = this.updateDetailData(item);
            const $detailBody = this.$refs.detailBody;
            if (util.getStyle($detailBody).position === 'relative') {
                $detailBody.scrollIntoView(false);
            }
            this.$emit('doMapEvent', function() {
                this.siteMap.setActiveMarker(
                    item.latitude,
                    item.longitude,
                    'school'
                );
            });
            if (
                !item.moreLink &&
                !item.moreLinkFetched &&
                item.name &&
                item.level
            ) {
                item.moreLinkFetched = true;
                this.axiosIns
                    .get(
                        `/api-site/listing-url-school?name=${encodeURIComponent(
                            item.label
                        )}&level=${encodeURIComponent(item.level)}`
                    )
                    .then(res => {
                        const data = (res && res.data) || {};
                        item.moreLink = detailData.moreLink = data.link;
                        item.moreLabel = detailData.moreLabel = data.label;
                        this.detailData = detailData;
                        this.showDetail = true;
                    })
                    .catch(e => {
                        console.log(e);
                        this.detailData = detailData;
                        this.showDetail = true;
                    });
            } else {
                detailData.moreLink = item.moreLink;
                detailData.moreLabel = item.moreLabel;
                this.detailData = detailData;
                this.showDetail = true;
            }
            if (this.isMobile) {
                this.itemEnter('school', item);
            }
        },
        updateDetailData(item) {
            const table = [
                {
                    label: this.$t('md-map.admission'),
                    value: item.admission
                },
                {
                    label: this.$t('md-map.level'),
                    value: `${item.gradeLow}-${item.gradeHigh}`
                }
            ];
            if (item.gsRating) {
                table.push({
                    label: this.$t('md-map.rating'),
                    value: item.gsRating
                });
            }
            table.push({
                label: this.$t('md-map.library'),
                value: item.library
                    ? this.$t('md-map.yes')
                    : this.$t('md-map.no')
            });
            const info = [];
            if (item.phone) {
                info.push({
                    label: this.$t('md-map.phone0'),
                    value: item.phone
                });
            }
            if (item.expenditure) {
                info.push({
                    label: this.$t('md-map.expenditure'),
                    value: item.expenditure
                });
            }
            info.push({
                label: this.$t('md-map.address'),
                value: `${item.addressStreet}, ${item.addressCity}, ${item.addressState}, ${item.addressZip}`
            });
            if (item.studentTeacherRatio) {
                info.push({
                    label: this.$t('md-map.ratio'),
                    value: this.$t('md-map.studentTeacherRatio', {
                        ratio: item.studentTeacherRatio
                    })
                });
            }
            return {
                name: item.name,
                table,
                info
            };
        },
        detailBack() {
            this.showDetail = false;
            this.itemLeave('school');
        },
        onMarkerHover(marker) {
            const originData = marker.getData(true) || [];
            this.itemEnter('school', originData[0], false);
        },
        onMarkerLeave() {
            this.itemLeave('school');
        },
        onMarkerClick(marker, type) {
            if (type === 'house') {
                this.itemActiveKey = 'house-' + marker.lat + '-' + marker.lng;
                this.$nextTick(() => {
                    this.itemScrollIntoView('.tab-content.house');
                });
            } else if (type === 'school') {
                this.itemActiveKey = 'school-' + marker.lat + '-' + marker.lng;
                const originData = marker.getData(true) || [];
                this.schoolItemClick(originData[0]);
            } else if (type === 'business') {
                this.itemActiveKey =
                    'business-' + marker.lat + '-' + marker.lng;
                if (this.businessGroupActiveIndex !== -1) {
                    this.$nextTick(() => {
                        this.itemScrollIntoView('.tab-content.business');
                    });
                } else {
                    const originData = marker.getData(true) || [];
                    let markerData = originData[0];
                    var business = this.tabs.find(g => g.type === 'business');
                    if (business) {
                        var groupIndex = business.groups.indexOf(
                            markerData.group
                        );
                        this.businessGroupClick(
                            groupIndex,
                            markerData.group,
                            true
                        );
                        this.$nextTick(() => {
                            this.itemScrollIntoView('.tab-content.business');
                        });
                    }
                }
            }
        },
        itemScrollIntoView(selector) {
            const $panel = this.$el.querySelector(selector);
            const $item = $panel && $panel.querySelector('.item.active');
            if ($item) {
                $panel.scrollTop = $item.offsetTop - 5;
            }
        },
        getDetailTarget(data) {
            return (
                data.target ||
                (data.soldDate
                    ? this.page.listingDetailSoldOpenStyle
                    : this.page.listingDetailOpenStyle) ||
                '_self'
            );
        },
        formatBed(num, Fixed) {
            return this.$t('md-map.bed', {
                count: num,
                num: numberFormat(num, false, Fixed)
            });
        },
        formatBath(num, Fixed) {
            return this.$t('md-map.bath', {
                count: num,
                num: numberFormat(num, false, Fixed)
            });
        },
        formatSqft(info, Fixed) {
            let text = '';
            if (info.propertyType == 'Vacant Land') {
                if (info.totalAvailableAcres > 0) {
                    text = this.$t('md-map.acres', {
                        num: numberFormat(
                            info.totalAvailableAcres / (10890 * 4),
                            false,
                            Fixed
                        )
                    });
                }
            } else {
                if (info.sqft > 0) {
                    text = this.$t('md-map.sqft', {
                        num: numberFormat(info.sqft, false, Fixed)
                    });
                } else if (info.totalAvailableAcres >= 10890) {
                    text = this.$t('md-map.acresLot', {
                        num: numberFormat(
                            info.totalAvailableAcres / (10890 * 4),
                            false,
                            Fixed
                        )
                    });
                } else if (
                    info.totalAvailableAcres > 0 &&
                    info.totalAvailableAcres < 10890
                ) {
                    text = this.$t('md-map.sqftLot', {
                        num: numberFormat(
                            info.totalAvailableAcres,
                            false,
                            Fixed
                        )
                    });
                }
            }
            return text;
        }
    }
};
